import { formatScoreline } from "../lib/matches.js";
import type { FixtureResult, FixtureSide } from "../lib/types.js";
import type { Person, Roster } from "../routes/event/shared.js";
import { AvatarPair } from "./Avatar.js";

/**
 * The draw as a list, grouped by round.
 *
 * Shared by the preview and the saved draw, which is the point — an organiser comparing
 * "what this would be" against "what it is" should not have to translate between two
 * layouts. A proposed fixture has no id and no result yet; everything else is identical, so
 * the shape below is the smaller of the two.
 */
export interface ListedFixture {
  id?: string;
  round: number;
  sequence: number;
  sideA: FixtureSide;
  sideB: FixtureSide;
  matchId?: string | null;
  result?: FixtureResult | null;
  /** Resolved players, with "winner of #3" followed through. See `fetchDraw`. */
  playersA?: string[] | null;
  playersB?: string[] | null;
}

export function FixtureList({
  fixtures,
  roster,
  canScore,
  onScore,
  onEdit,
  eventCountsForRating = true,
}: {
  fixtures: ListedFixture[];
  roster: Roster;
  /** Whether this fixture is yours to enter a score for. Nothing offers it if absent. */
  canScore?: (fixture: ListedFixture) => boolean;
  onScore?: (fixture: ListedFixture) => void;
  /**
   * Change who plays this one, or take it out. Organisers only — passed in rather than
   * decided here, and offered only on a fixture that is still to play and whose sides are
   * people rather than "winner of #3", which are the two things `edit_fixture` insists on.
   */
  onEdit?: (fixture: ListedFixture) => void;
  /**
   * Whether the event counts towards ratings. When it does not, the screen says so once for
   * the whole event and no row repeats it; when it does, the odd match that still did not
   * rate — a mixed one — is marked on its own row.
   */
  eventCountsForRating?: boolean;
}) {
  // On one court every fixture is its own round, and a "Round 7" heading above every single
  // line is a heading that says nothing. The rounds are only worth naming when they group.
  const rounds = new Set(fixtures.map((fixture) => fixture.round)).size;
  const showRounds = rounds > 1 && rounds < fixtures.length;

  return (
    <ol className="mt-3">
      {fixtures.map((fixture, index) => {
        const result = fixture.result ?? null;
        const scorable = !result && canScore?.(fixture) === true && onScore;
        const editable =
          !result &&
          fixture.id !== undefined &&
          fixture.sideA.kind === "players" &&
          fixture.sideB.kind === "players" &&
          onEdit;

        return (
          <li key={fixture.sequence}>
            {showRounds && fixture.round !== fixtures[index - 1]?.round ? (
              <p className="micro mt-3">Round {fixture.round}</p>
            ) : null}
            <div
              className="flex items-center justify-between gap-3 border-t py-2"
              style={{ borderColor: "var(--color-line-muted)" }}
            >
              <div className="min-w-0">
                <Side
                  side={fixture.sideA}
                  players={fixture.playersA}
                  roster={roster}
                  beaten={result?.winner === "B"}
                />
                <Side
                  side={fixture.sideB}
                  players={fixture.playersB}
                  roster={roster}
                  beaten={result?.winner === "A"}
                  prefix="v "
                />
              </div>

              <span className="flex shrink-0 items-center gap-3">
                {/* One slot, and what it holds says where the fixture is: a way in while it
                    is still to play, the score once it has been played. */}
                {result ? (
                  <span className="font-mono text-sm">
                    {formatScoreline(result.games)}
                    {result.status === "disputed" ? (
                      <span className="micro ml-2" style={{ color: "var(--color-attention)" }}>
                        disputed
                      </span>
                    ) : null}
                    {eventCountsForRating && !result.movesRatings ? (
                      <span className="micro ml-2" style={{ color: "var(--color-ink-muted)" }}>
                        unrated
                      </span>
                    ) : null}
                  </span>
                ) : scorable ? (
                  <button
                    type="button"
                    onClick={() => onScore(fixture)}
                    className="micro underline"
                    style={{ minHeight: 44, paddingLeft: 12 }}
                  >
                    Score
                  </button>
                ) : (
                  <span className="micro">#{fixture.sequence}</span>
                )}

                {/* Second, and quieter than Score. Somebody courtside is entering a result;
                    rearranging the draw is the rarer errand and should not be the nearer
                    button. */}
                {editable ? (
                  <button
                    type="button"
                    onClick={() => onEdit(fixture)}
                    className="micro underline"
                    style={{ minHeight: 44, color: "var(--color-ink-muted)" }}
                    aria-label={`Edit match ${fixture.sequence}`}
                  >
                    Edit
                  </button>
                ) : null}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * One side: the faces, then the names.
 *
 * A knockout slot says "winner of #3" until #3 has been played and then says who that was —
 * the bracket is only readable forwards once the names arrive, and until then there are no
 * faces to show either. The loser goes muted rather than struck through: the match happened,
 * and both of them played it.
 */
function Side({
  side,
  players,
  roster,
  beaten,
  prefix,
}: {
  side: FixtureSide;
  players: string[] | null | undefined;
  roster: Roster;
  beaten: boolean;
  prefix?: string;
}) {
  const people = known(side, players, roster);

  return (
    <div
      className={`flex items-center gap-2 ${prefix ? "mt-1" : ""}`}
      style={beaten ? { color: "var(--color-ink-muted)" } : undefined}
    >
      {people ? <AvatarPair people={people} size={22} /> : null}
      <span className="truncate text-sm">
        {prefix}
        {people ? people.map((person) => person.name).join(" & ") : describe(side)}
      </span>
    </div>
  );
}

/** The people on this side, or null when it is a bye or a slot still waiting on a result. */
function known(
  side: FixtureSide,
  players: string[] | null | undefined,
  roster: Roster,
): Person[] | null {
  if (side.kind === "bye") return null;
  if (side.kind === "winner") {
    return players?.length ? players.map((id) => roster.get(id) ?? unknown) : null;
  }
  return side.memberIds.map((id) => roster.get(id) ?? unknown);
}

// A name missing from the roster means the entrant withdrew after the draw was made. The
// fixture is still real and still has to be played or redrawn, so show that rather than a
// blank.
const unknown: Person = { name: "withdrawn", url: null };

const describe = (side: FixtureSide) =>
  side.kind === "bye" ? "bye" : side.kind === "winner" ? `winner of #${side.of}` : "";
